import type { ProviderConfig } from './types';

const PROVIDERS: Record<string, ProviderConfig> = {
  openai: {
    name: 'OpenAI',
    logo: '/logos/openai.svg',
    color: 'emerald',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        placeholder: 'sk-...',
        description: 'Your OpenAI API key starting with "sk-"'
      }
    ],
    // Models are now fetched dynamically from /credentials/available-models
    instructions: {
      title: 'How to get your OpenAI API Key',
      steps: [
        'Go to OpenAI Platform → API Keys',
        'Click "Create new secret key"',
        'Copy the key immediately (it won\'t be shown again)',
        'The key starts with "sk-proj-" or "sk-"'
      ],
      link: 'https://platform.openai.com/api-keys',
      linkText: 'Open OpenAI API Keys',
      permissions: 'Ensure your API key has access to the models you want to test (GPT-4o, GPT-4o-mini, etc.)'
    }
  },
  anthropic: {
    name: 'Anthropic',
    logo: '/logos/anthropic.svg',
    color: 'orange',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        placeholder: 'sk-ant-...',
        description: 'Your Anthropic API key starting with "sk-ant-"'
      }
    ],
    // Models are now fetched dynamically from /credentials/available-models
    instructions: {
      title: 'How to get your Anthropic API Key',
      steps: [
        'Go to Anthropic Console → API Keys',
        'Click "Create Key"',
        'Give it a name and copy the key',
        'The key starts with "sk-ant-api03-"'
      ],
      link: 'https://console.anthropic.com/settings/keys',
      linkText: 'Open Anthropic Console',
      permissions: 'Your workspace must have credits or a billing method configured to use the API.'
    }
  },
  bedrock: {
    name: 'AWS Bedrock',
    logo: '/logos/aws.svg',
    color: 'amber',
    authMethods: [
      { value: 'account_connection', label: 'Use Existing AWS Connection (Recommended)' },
      { value: 'role_assumption', label: 'IAM Role Assumption' },
      { value: 'access_keys', label: 'Access Keys' },
    ],
    fields: {
      account_connection: [
        {
          key: 'aws_account_id',
          label: 'AWS Account',
          type: 'aws_account_select',
          description: 'Select an AWS account already configured in your platform settings'
        },
        {
          key: 'region',
          label: 'Region',
          type: 'text',
          placeholder: 'us-east-1',
          default: 'us-east-1',
          description: 'AWS region where Bedrock is enabled (e.g., us-east-1, us-west-2)'
        },
      ],
      role_assumption: [
        {
          key: 'role_arn',
          label: 'Role ARN',
          type: 'text',
          placeholder: 'arn:aws:iam::123456789:role/BedrockAccess',
          description: 'The ARN of the IAM role that Aivilo will assume'
        },
        {
          key: 'external_id',
          label: 'External ID (Optional)',
          type: 'text',
          placeholder: 'Optional security identifier',
          description: 'An optional security identifier for the trust relationship'
        },
        {
          key: 'region',
          label: 'Region',
          type: 'text',
          placeholder: 'us-east-1',
          default: 'us-east-1',
          description: 'AWS region where Bedrock is enabled (e.g., us-east-1, us-west-2)'
        },
        {
          key: 'evaluation_role_arn',
          label: 'Evaluation Role ARN (Optional)',
          type: 'text',
          required: false,
          placeholder: 'arn:aws:iam::123456789:role/BedrockEvaluationRole',
          description: 'IAM role for Bedrock Evaluation Jobs (must trust bedrock.amazonaws.com)'
        },
        {
          key: 'evaluation_output_bucket',
          label: 'Evaluation Output Bucket (Optional)',
          type: 'text',
          required: false,
          placeholder: 'my-bedrock-evaluation-outputs',
          description: 'S3 bucket for storing Bedrock evaluation results'
        },
      ],
      access_keys: [
        {
          key: 'access_key_id',
          label: 'Access Key ID',
          type: 'text',
          placeholder: 'AKIA...',
          description: 'IAM user access key ID starting with "AKIA"'
        },
        {
          key: 'secret_access_key',
          label: 'Secret Access Key',
          type: 'password',
          placeholder: 'Your secret key',
          description: 'The secret access key paired with the Access Key ID'
        },
        {
          key: 'region',
          label: 'Region',
          type: 'text',
          placeholder: 'us-east-1',
          default: 'us-east-1',
          description: 'AWS region where Bedrock is enabled (e.g., us-east-1, us-west-2)'
        },
        {
          key: 'evaluation_role_arn',
          label: 'Evaluation Role ARN (Optional)',
          type: 'text',
          required: false,
          placeholder: 'arn:aws:iam::123456789:role/BedrockEvaluationRole',
          description: 'IAM role for Bedrock Evaluation Jobs (must trust bedrock.amazonaws.com)'
        },
        {
          key: 'evaluation_output_bucket',
          label: 'Evaluation Output Bucket (Optional)',
          type: 'text',
          required: false,
          placeholder: 'my-bedrock-evaluation-outputs',
          description: 'S3 bucket for storing Bedrock evaluation results'
        },
      ],
    },
    // Models are now fetched dynamically from /credentials/available-models
    instructions: {
      account_connection: {
        title: 'Use Existing AWS Connection',
        steps: [
          'Select the AWS account already configured in your platform Settings > AWS Accounts',
          'Choose the region where Bedrock is enabled',
          'Test the connection to verify Bedrock access',
        ],
        permissions: `The existing cross-account role must include Bedrock permissions:
• bedrock:InvokeModel - Run model inference
• bedrock:ListFoundationModels - Discover available models
• bedrock:ListInferenceProfiles - Cross-region inference

If permissions are missing, update the IAM role policy in your AWS account.`,
      },
      role_assumption: {
        title: 'How to configure IAM Role Assumption',
        steps: [
          'Create an IAM Role in your AWS account',
          'Attach the required permissions (see Required Permissions below)',
          'Configure the trust relationship to allow Aivilo\'s AWS account to assume the role',
          'Copy the Role ARN (e.g., arn:aws:iam::123456789012:role/BedrockAccessRole)',
          'Optionally, set an External ID for additional security'
        ],
        link: 'https://docs.aws.amazon.com/bedrock/latest/userguide/security-iam.html',
        linkText: 'AWS Bedrock IAM Documentation',
        permissions: `Required permissions:
• bedrock:InvokeModel - Run model inference
• bedrock:InvokeModelWithResponseStream - Streaming responses
• bedrock:ListFoundationModels - Discover available models
• bedrock:ListInferenceProfiles - Cross-region inference (Claude 4.x)

For Bedrock Evaluations (optional):
• bedrock:CreateEvaluationJob, bedrock:GetEvaluationJob, bedrock:StopEvaluationJob
• s3:PutObject, s3:GetObject on your evaluation bucket

For API Usage Monitoring (optional):
• servicequotas:ListServiceQuotas, servicequotas:GetServiceQuota
• cloudwatch:GetMetricData`,
        warning: 'Contact Aivilo support for the AWS account ID to add to your trust policy.'
      },
      access_keys: {
        title: 'How to configure Access Keys',
        steps: [
          'Go to AWS IAM Console → Users',
          'Create a new IAM user or select an existing one',
          'Attach the required permissions (see Required Permissions below)',
          'Go to Security Credentials tab → Create access key',
          'Select "Third-party service" as the use case',
          'Copy both the Access Key ID (starts with AKIA) and Secret Access Key'
        ],
        link: 'https://console.aws.amazon.com/iam/home#/users',
        linkText: 'Open AWS IAM Console',
        permissions: `Required permissions:
• bedrock:InvokeModel - Run model inference
• bedrock:InvokeModelWithResponseStream - Streaming responses
• bedrock:ListFoundationModels - Discover available models
• bedrock:ListInferenceProfiles - Cross-region inference (Claude 4.x)

For Bedrock Evaluations (optional):
• bedrock:CreateEvaluationJob, bedrock:GetEvaluationJob, bedrock:StopEvaluationJob
• s3:PutObject, s3:GetObject on your evaluation bucket

For API Usage Monitoring (optional):
• servicequotas:ListServiceQuotas, servicequotas:GetServiceQuota
• cloudwatch:GetMetricData`,
        warning: 'Use long-term access keys (starting with AKIA), not temporary SSO credentials (ASIA).'
      }
    }
  },
  // Self-hosted / Custom providers
  ollama: {
    name: 'Ollama',
    logo: null,
    color: 'purple',
    fields: [
      {
        key: 'endpoint_url',
        label: 'Endpoint URL',
        type: 'text',
        placeholder: 'http://localhost:11434',
        default: 'http://localhost:11434',
        description: 'Ollama server URL (default: http://localhost:11434)'
      },
      {
        key: 'default_model',
        label: 'Default Model (Optional)',
        type: 'text',
        placeholder: 'llama2',
        description: 'Default model to use if not specified (e.g., llama2, mistral, codellama)'
      }
    ],
    instructions: {
      title: 'How to set up Ollama',
      steps: [
        'Install Ollama from https://ollama.ai',
        'Run "ollama serve" to start the server',
        'Pull a model: "ollama pull llama2"',
        'The server runs on http://localhost:11434 by default'
      ],
      link: 'https://ollama.ai',
      linkText: 'Ollama Documentation',
      permissions: 'No authentication required for local Ollama instances.'
    }
  },
  vllm: {
    name: 'vLLM',
    logo: null,
    color: 'indigo',
    fields: [
      {
        key: 'endpoint_url',
        label: 'Endpoint URL',
        type: 'text',
        placeholder: 'http://localhost:8000',
        default: 'http://localhost:8000',
        description: 'vLLM server URL (default: http://localhost:8000)'
      },
      {
        key: 'api_key',
        label: 'API Key (Optional)',
        type: 'password',
        placeholder: 'Leave empty if not required',
        description: 'API key if your vLLM deployment requires authentication'
      },
      {
        key: 'default_model',
        label: 'Model Name',
        type: 'text',
        placeholder: 'meta-llama/Llama-2-7b-chat-hf',
        description: 'The model loaded in vLLM (must match the --model argument)'
      }
    ],
    instructions: {
      title: 'How to set up vLLM',
      steps: [
        'Install vLLM: pip install vllm',
        'Start the server: python -m vllm.entrypoints.openai.api_server --model meta-llama/Llama-2-7b-chat-hf',
        'The server provides OpenAI-compatible API at /v1/chat/completions',
        'For multi-GPU: add --tensor-parallel-size N'
      ],
      link: 'https://docs.vllm.ai/en/latest/',
      linkText: 'vLLM Documentation',
      permissions: 'Requires a Hugging Face token for gated models (e.g., Llama).'
    }
  },
  openai_compatible: {
    name: 'OpenAI Compatible',
    logo: null,
    color: 'teal',
    fields: [
      {
        key: 'endpoint_url',
        label: 'Endpoint URL',
        type: 'text',
        placeholder: 'https://your-endpoint.com',
        description: 'Base URL of the OpenAI-compatible API (e.g., LiteLLM, LocalAI, Together AI)'
      },
      {
        key: 'api_key',
        label: 'API Key (Optional)',
        type: 'password',
        placeholder: 'Your API key',
        description: 'API key for authentication (if required by the endpoint)'
      },
      {
        key: 'default_model',
        label: 'Default Model',
        type: 'text',
        placeholder: 'gpt-3.5-turbo',
        description: 'Model identifier as expected by the endpoint'
      }
    ],
    instructions: {
      title: 'OpenAI-Compatible Endpoints',
      steps: [
        'Any API that implements the OpenAI chat completions format',
        'Must support POST /v1/chat/completions endpoint',
        'Examples: LiteLLM, LocalAI, Together AI, Fireworks AI, Groq',
        'Enter the base URL without /v1/chat/completions'
      ],
      link: 'https://platform.openai.com/docs/api-reference/chat',
      linkText: 'OpenAI API Reference',
      permissions: 'Authentication requirements vary by provider.'
    }
  },
  sagemaker: {
    name: 'AWS SageMaker',
    logo: '/logos/aws.svg',
    color: 'orange',
    authMethods: [
      { value: 'role_assumption', label: 'IAM Role Assumption (Recommended)' },
      { value: 'access_keys', label: 'Access Keys' },
      { value: 'default', label: 'Default Credentials (IAM Role/Env)' },
    ],
    fields: {
      role_assumption: [
        {
          key: 'endpoint_name',
          label: 'Endpoint Name',
          type: 'text',
          placeholder: 'my-llm-endpoint',
          description: 'The name of your SageMaker endpoint'
        },
        {
          key: 'region',
          label: 'Region',
          type: 'text',
          placeholder: 'us-east-1',
          default: 'us-east-1',
          description: 'AWS region where the endpoint is deployed'
        },
        {
          key: 'role_arn',
          label: 'Role ARN',
          type: 'text',
          placeholder: 'arn:aws:iam::123456789:role/SageMakerInvokeRole',
          description: 'IAM role ARN to assume for invoking the endpoint'
        },
        {
          key: 'external_id',
          label: 'External ID (Optional)',
          type: 'text',
          placeholder: 'Optional security identifier',
          description: 'External ID for the trust relationship'
        }
      ],
      access_keys: [
        {
          key: 'endpoint_name',
          label: 'Endpoint Name',
          type: 'text',
          placeholder: 'my-llm-endpoint',
          description: 'The name of your SageMaker endpoint'
        },
        {
          key: 'region',
          label: 'Region',
          type: 'text',
          placeholder: 'us-east-1',
          default: 'us-east-1',
          description: 'AWS region where the endpoint is deployed'
        },
        {
          key: 'access_key_id',
          label: 'Access Key ID',
          type: 'text',
          placeholder: 'AKIA...',
          description: 'IAM user access key ID'
        },
        {
          key: 'secret_access_key',
          label: 'Secret Access Key',
          type: 'password',
          placeholder: 'Your secret key',
          description: 'IAM user secret access key'
        }
      ],
      default: [
        {
          key: 'endpoint_name',
          label: 'Endpoint Name',
          type: 'text',
          placeholder: 'my-llm-endpoint',
          description: 'The name of your SageMaker endpoint'
        },
        {
          key: 'region',
          label: 'Region',
          type: 'text',
          placeholder: 'us-east-1',
          default: 'us-east-1',
          description: 'AWS region where the endpoint is deployed'
        }
      ]
    },
    instructions: {
      role_assumption: {
        title: 'How to configure SageMaker with IAM Role',
        steps: [
          'Deploy your LLM to a SageMaker endpoint',
          'Create an IAM role with sagemaker:InvokeEndpoint permission',
          'Configure the trust relationship for cross-account access',
          'Copy the endpoint name and role ARN'
        ],
        link: 'https://docs.aws.amazon.com/sagemaker/latest/dg/realtime-endpoints.html',
        linkText: 'SageMaker Endpoints Documentation',
        permissions: 'Required: sagemaker:InvokeEndpoint, sagemaker:DescribeEndpoint'
      },
      access_keys: {
        title: 'How to configure SageMaker with Access Keys',
        steps: [
          'Deploy your LLM to a SageMaker endpoint',
          'Create an IAM user with sagemaker:InvokeEndpoint permission',
          'Generate access keys for the user',
          'Copy the endpoint name and credentials'
        ],
        link: 'https://docs.aws.amazon.com/sagemaker/latest/dg/realtime-endpoints.html',
        linkText: 'SageMaker Endpoints Documentation',
        permissions: 'Required: sagemaker:InvokeEndpoint, sagemaker:DescribeEndpoint'
      },
      default: {
        title: 'Using Default AWS Credentials',
        steps: [
          'Ensure the server has AWS credentials configured',
          'Can use IAM role (EC2/ECS), environment variables, or ~/.aws/credentials',
          'The credentials must have sagemaker:InvokeEndpoint permission',
          'Only enter the endpoint name and region'
        ],
        link: 'https://docs.aws.amazon.com/cli/latest/userguide/cli-configure-files.html',
        linkText: 'AWS Credentials Documentation',
        permissions: 'Uses credentials from the server environment.'
      }
    }
  },
};

export default PROVIDERS;
